import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../store';
import { fetchCaseDraftTypes, updateCaseDraftTypes, createDraftTemplate } from '../../../store/actions/Drafts/DraftTypesActions';
import { showFeedbackModal } from '../../../store/actions/UserFeedbackActions';
import Button from '../../shared/TailwindComponents/Button';
import Input from '../../shared/TailwindComponents/Input';
import Textarea from '../../shared/TailwindComponents/Textarea';
import DraftTemplateManager from './DraftTemplateManager';
import DraftTypeSelector from './DraftTypeSelector';

interface DraftType {
  Description: string;
  DraftID: number;
  IsActive: boolean;
  Name: string;
  ParentDraftID: number | null;
  Status: string;
}

interface DraftTypeModalProps {
  caseId: string;
  onClose: () => void;
  onSave: () => void;
}

const DraftTypeModal: React.FC<DraftTypeModalProps> = ({ caseId, onClose, onSave }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [allDraftTypes, setAllDraftTypes] = useState<DraftType[]>([]);
  const [selectedDraftTypeIds, setSelectedDraftTypeIds] = useState<number[]>([]);
  const [initialSelectedDraftTypeIds, setInitialSelectedDraftTypeIds] = useState<number[]>([]);
  const [showSaveTemplateForm, setShowSaveTemplateForm] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [viewMode, setViewMode] = useState<'draftTypes' | 'templates'>('draftTypes');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    dispatch(fetchCaseDraftTypes(caseId)).then((data) => {
      setAllDraftTypes(data);
      const activeDraftTypeIds = data.filter((dt: DraftType) => dt.IsActive).map((dt: DraftType) => dt.DraftID);
      setSelectedDraftTypeIds(activeDraftTypeIds);
      setInitialSelectedDraftTypeIds(activeDraftTypeIds);
    }).finally(() => setLoading(false));
  }, [caseId, dispatch]);

  const handleSave = () => {
    dispatch(updateCaseDraftTypes(caseId, selectedDraftTypeIds))
      .then(() => {
        if (onSave) onSave();
        onClose();
      })
      .catch(() => {});
  };

  const handleSaveAsTemplate = () => {
    if (!templateName) {
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Please enter a template name.'),
        modalType: 'error',
        duration: 3000,
      }));
      return;
    }
    dispatch(createDraftTemplate({ name: templateName, description: templateDescription, draftTypeIds: selectedDraftTypeIds }))
      .then(() => {
        setShowSaveTemplateForm(false);
        setTemplateName('');
        setTemplateDescription('');
      });
  };

  const hasChanges = useMemo(
    () => JSON.stringify(selectedDraftTypeIds.sort()) !== JSON.stringify(initialSelectedDraftTypeIds.sort()),
    [selectedDraftTypeIds, initialSelectedDraftTypeIds]
  );

  return (
    <div className="w-full mx-auto p-6 bg-white dark:bg-gray-900 mt-2">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
          {viewMode === 'draftTypes' ? t('Add Case Documents') : t('Document Templates')}
        </h2>
        <div className="flex items-center gap-2">
          {viewMode === 'draftTypes' && (
            <>
              <Button onClick={() => setShowSaveTemplateForm(true)} variant="outline" size="small">
                {t('Save as Template')}
              </Button>
              <Button onClick={() => setViewMode('templates')} variant="outline" size="small">
                {t('Browse Templates')}
              </Button>
              <Button
                onClick={handleSave}
                variant="primary"
                size="small"
                disabled={!hasChanges}
                className={`${hasChanges ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-400 cursor-not-allowed'} text-white`}
              >
                {t('Save')}
              </Button>
            </>
          )}
          {viewMode === 'templates' && (
            <>
              <Button onClick={() => setViewMode('draftTypes')} variant="outline" size="small">
                {t('Back to Document Types')}
              </Button>
            </>
          )}
        </div>
      </div>

      {viewMode === 'draftTypes' ? (
        <>
          {showSaveTemplateForm && (
            <div className="mt-4 rounded">
              <h3 className="text-lg font-medium mb-2">{t('Save as Template')}</h3>
              <Input
                type="text"
                placeholder={t('Template Name')}
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                className="mb-2"
              />
              <Textarea
                placeholder={t('Description (optional)')}
                value={templateDescription}
                onChange={(e) => setTemplateDescription(e.target.value)}
                rows={2}
                id=""
                label=""
              />
              <div className="mt-2 flex justify-end gap-2">
                <Button onClick={() => setShowSaveTemplateForm(false)} variant="outline" size="small">
                  {t('Cancel')}
                </Button>
                <Button
                  onClick={handleSaveAsTemplate}
                  variant="primary"
                  size="small"
                  disabled={!templateName}
                >
                  {t('Save')}
                </Button>
              </div>
            </div>
          )}
          {loading ? (
            <div className="text-center text-gray-500 dark:text-gray-400">Loading...</div>
          ) : (
            <DraftTypeSelector
              allDraftTypes={allDraftTypes}
              selectedDraftTypeIds={selectedDraftTypeIds}
              onChange={setSelectedDraftTypeIds}
            />
          )}
        </>
      ) : (
        <DraftTemplateManager
          caseId={caseId} // Pass caseId
          onClose={() => setViewMode('draftTypes')}
          onSelectTemplate={(draftTypeIds) => {
            setSelectedDraftTypeIds(draftTypeIds);
            setViewMode('draftTypes');
          }}
        />
      )}
    </div>
  );
};

export default DraftTypeModal;